import React, { useState } from "react";
import contractsV2Service from "../services/contractsV2Service";
import { Contract } from "../types/contract";

export const useInlineEditing = (onSaveSuccess?: () => Promise<void>) => {
  const [editingCell, setEditingCell] = useState<{conId: number; key: keyof Contract; value: string} | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Map<number, Record<string, {oldValue: string; newValue: string}>>>(new Map());
  const [savingChanges, setSavingChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleCellDoubleClick = (contract: Contract, key: keyof Contract) => {
    // Don't allow editing conId or system fields
    if (key === "id" || key === "conId" || key === "createdAt" || key === "updatedAt") return;
    const pending = pendingChanges.get(contract.conId)?.[key];
    const value = pending ? pending.newValue : contract[key];
    setEditingCell({
      conId: contract.conId,
      key,
      value: value != null ? String(value) : ""
    });
  };

  const handleCellChange = (value: string) => {
    if (!editingCell) return;
    setEditingCell({ ...editingCell, value });
  };

  const handleCellBlur = (contracts: Contract[]) => {
    if (!editingCell) return;
    
    const contract = contracts.find(c => c.conId === editingCell.conId);
    if (!contract) {
      setEditingCell(null);
      return;
    }

    const oldValue = contract[editingCell.key];
    const oldStr = oldValue != null ? String(oldValue) : "";
    const newStr = editingCell.value;

    // Only track if value actually changed
    if (oldStr !== newStr) {
      setPendingChanges(prev => {
        const next = new Map(prev);
        const existing = next.get(editingCell.conId) || {};
        existing[editingCell.key] = { oldValue: oldStr, newValue: newStr };
        next.set(editingCell.conId, existing);
        return next;
      });
    }

    setEditingCell(null);
  };

  const handleCellKeyDown = (e: React.KeyboardEvent, contracts: Contract[]) => {
    if (e.key === "Escape") {
      setEditingCell(null);
    } else if (e.key === "Enter") {
      handleCellBlur(contracts);
    }
  };

  const saveChanges = async (contracts: Contract[]) => {
    if (pendingChanges.size === 0) return;
    
    setSavingChanges(true);
    setSaveError(null);
    try {
      for (const [conId, changes] of pendingChanges) {
        const contract = contracts.find(c => c.conId === conId);
        if (!contract) continue;

        // Build updated contract
        const input: any = {
          conId: contract.conId,
          symbol: contract.symbol,
          secType: contract.secType,
          currency: contract.currency,
          exchange: contract.exchange,
          primaryExchange: contract.primaryExchange,
          longName: contract.longName,
        };

        // Apply changes
        for (const [key, { newValue }] of Object.entries(changes)) {
          if (["minTick", "minSize", "sizeIncrement"].includes(key)) {
            input[key] = newValue ? parseFloat(newValue) : null;
          } else if (["optionAllowed", "shortable"].includes(key)) {
            input[key] = newValue === "true" || newValue === "Yes";
          } else {
            input[key] = newValue || null;
          }
        }

        await contractsV2Service.upsertContract(input);
      }

      setPendingChanges(new Map());
      if (onSaveSuccess) await onSaveSuccess();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save changes");
    } finally {
      setSavingChanges(false);
    }
  };

  const discardChanges = () => {
    setPendingChanges(new Map());
  };

  return {
    editingCell,
    pendingChanges,
    savingChanges,
    saveError,
    handleCellDoubleClick,
    handleCellChange,
    handleCellBlur,
    handleCellKeyDown,
    saveChanges,
    discardChanges
  };
};
